import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';

// Email validation regex pattern
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Cloudflare Turnstile site key
const TURNSTILE_SITE_KEY = '0x4AAAAAABgceIXtdb0T2uGy';

// API endpoint for form submission
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
if (!API_ENDPOINT) {
  throw new Error('REACT_APP_API_ENDPOINT is not defined');
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  currentChallenges: string;
  projectScale: string;
  timeline: string;
  budget: string;
  privacy: boolean;
}

interface FormErrors {
  name: string;
  email: string;
  phone: string;
  currentChallenges: string;
  projectScale: string;
  timeline: string;
  budget: string;
  turnstile: string;
  submit: string;
  privacy: string;
}

declare global {
  interface Window {
    turnstile: {
      render: (container: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
    };
  }
}

const TechnicalAssessment = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    currentChallenges: '',
    projectScale: '',
    timeline: '',
    budget: '',
    privacy: false,
  });

  const [errors, setErrors] = useState<FormErrors>({
    name: '',
    email: '',
    phone: '',
    currentChallenges: '',
    projectScale: '',
    timeline: '',
    budget: '',
    turnstile: '',
    submit: '',
    privacy: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const scriptLoaded = useRef(false);

  useEffect(() => {
    if (!scriptLoaded.current) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      scriptLoaded.current = true;

      script.onload = () => {
        if (window.turnstile && !turnstileWidgetId.current) {
          turnstileWidgetId.current = window.turnstile.render('#turnstile-container', {
            sitekey: TURNSTILE_SITE_KEY,
            theme: 'dark',
            callback: (token: string) => {
              setTurnstileToken(token);
              setErrors((prev) => ({
                ...prev,
                turnstile: '',
              }));
            },
          });
        }
      };
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({ ...formData, [name]: checked });
      if (checked && name === 'privacy') {
        setErrors({ ...errors, [name]: '' });
      }
    } else {
      setFormData({ ...formData, [name]: value });
      if (value.trim() && errors[name as keyof FormErrors]) {
        setErrors({ ...errors, [name]: '' });
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      name: '',
      email: '',
      phone: '',
      currentChallenges: '',
      projectScale: '',
      timeline: '',
      budget: '',
      turnstile: '',
      submit: '',
      privacy: '',
    };

    if (!formData.name.trim()) {
      newErrors.name = 'Please enter your name';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Please enter a valid UK telephone number'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.currentChallenges.trim()) {
      newErrors.currentChallenges = 'Please describe your current challenges';
    }

    // if (!formData.projectScale.trim()) {
    //   newErrors.projectScale = 'Please select project scale';
    // }

    // if (!formData.timeline.trim()) {
    //   newErrors.timeline = 'Please select timeline';
    // }

    if (!formData.privacy) {
      newErrors.privacy = 'You must accept the privacy policy';
    }

    if (!turnstileToken) {
      newErrors.turnstile = 'Please complete the security check';
    }

    setErrors(newErrors);
    return Object.values(newErrors).every(error => error === '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors(prev => ({ ...prev, submit: '' }));

    try {
      const submissionData = {
        type: 'technical_assessment',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        currentChallenges: formData.currentChallenges,
        projectScale: formData.projectScale,
        timeline: formData.timeline,
        budget: formData.budget,
        privacy: formData.privacy,
        'cf-turnstile-response': turnstileToken,
      };

      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        currentChallenges: '',
        projectScale: '',
        timeline: '',
        budget: '',
        privacy: false,
      });
      setTurnstileToken(null);
      
      // Reset Turnstile widget
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId.current);
      }

    } catch (error) {
      setErrors(prev => ({
        ...prev,
        submit: 'There was an error submitting your request. Please try again or contact us directly.',
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <>
        <Helmet>
          <title>Thank You | Technical Assessment Request Submitted</title>
        </Helmet>
        <div className="relative min-h-screen overflow-hidden flex items-center justify-center">
          <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-dark-lighter to-black pointer-events-none z-0" />
          <div className="container mx-auto py-16 px-4 relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Thank You!
              </h1>
              <p className="text-xl text-primary mb-8">
                Your technical assessment request has been submitted successfully.
              </p>
              <div className="bg-white/5 backdrop-blur-lg border-2 border-accent/20 shadow-2xl rounded-lg p-8 max-w-2xl mx-auto">
                <h2 className="text-2xl font-bold text-white mb-4">What happens next?</h2>
                <ul className="text-gray-300 space-y-3 text-left">
                  <li>• Our technical team will review your submission and get back to you soon.</li>
                  <li>• We'll schedule a discovery call to discuss your challenges in detail and next steps.</li>
                  <li>• You'll receive a bespoke comprehensive technical assessment document with recommendations.</li>
                </ul>
                <p className="text-primary mt-6 font-semibold">
                  Technical Assessments are evaluated against a set of evaluation criteria to ensure we can provide the best possible recommendations for your business. 
                  We aren't able to provide assessments for all submissions, but we will always provide feedback and next steps.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Free Technical Assessment | nautdigital</title>
        <meta name="description" content="Get a free comprehensive technical assessment and recommendation document from our experts. Discover opportunities to optimize your technology stack and processes." />
      </Helmet>
      <div className="relative min-h-screen overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-dark-lighter to-black pointer-events-none z-0" />
        <div className="container mx-auto py-16 px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Technical Assessment
              </h1>
              <p className="text-xl text-primary mb-6">
                Get expert insights and recommendations on how to unlock your business through technology
              </p>
              <p className="text-gray-300 max-w-3xl mx-auto mb-8">
                Our comprehensive technical assessment will analyse your current technology stack, identify opportunities for improvement, and provide actionable recommendations to enhance efficiency, scalability, and performance.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                <div className="bg-white/5 backdrop-blur-lg border-2 border-accent/20 shadow-2xl rounded-lg p-6">
                  <div className="text-3xl mb-3">🔍</div>
                  <h3 className="text-lg font-bold text-white mb-2">Deep Analysis</h3>
                  <p className="text-gray-400 text-sm">Comprehensive review of your processes</p>
                </div>
                <div className="bg-white/5 backdrop-blur-lg border-2 border-accent/20 shadow-2xl rounded-lg p-6">
                  <div className="text-3xl mb-3">📋</div>
                  <h3 className="text-lg font-bold text-white mb-2">Detailed Report</h3>
                  <p className="text-gray-400 text-sm">Tailored document with our findings and recommendations</p>
                </div>
                <div className="bg-white/5 backdrop-blur-lg border-2 border-accent/20 shadow-2xl rounded-lg p-6">
                  <div className="text-3xl mb-3">🆓</div>
                  <h3 className="text-lg font-bold text-white mb-2">No obligation</h3>
                  <p className="text-gray-400 text-sm">No cost, no obligations - just valuable insights</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
              <div className="bg-white/5 backdrop-blur-lg border-2 border-accent/20 shadow-2xl rounded-lg p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Tell us about your business</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-1 gap-6 mb-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                      Full Name (Required)
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-dark border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary"
                    />
                    {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Contact Email (Required)
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-dark border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary"
                    />
                    {errors.email && <p className="text-red-400 text-sm mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                      Phone Number (Required)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-dark border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary"
                    />
                    {errors.phone && <p className="text-red-400 text-sm mt-1">{errors.phone}</p>}
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="currentChallenges" className="block text-sm font-medium text-gray-300 mb-2">
                    What are your current technical challenges? *
                  </label>
                  <textarea
                    id="currentChallenges"
                    name="currentChallenges"
                    value={formData.currentChallenges}
                    onChange={handleInputChange}
                    rows={4}
                    placeholder="Describe the technical challenges, pain points, or areas where you'd like expert guidance..."
                    className="w-full px-4 py-3 bg-dark border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary"
                  />
                  {errors.currentChallenges && <p className="text-red-400 text-sm mt-1">{errors.currentChallenges}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="projectScale" className="block text-sm font-medium text-gray-300 mb-2">
                      Project Scale
                    </label>
                    <select
                      id="projectScale"
                      name="projectScale"
                      value={formData.projectScale}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-dark border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary"
                    >
                      <option value="">Select scale</option>
                      <option value="small">Small (1-10 users)</option>
                      <option value="medium">Medium (10-100 users)</option>
                      <option value="large">Large (100-1000 users)</option>
                      <option value="enterprise">Enterprise (1000+ users)</option>
                    </select>
                    {errors.projectScale && <p className="text-red-400 text-sm mt-1">{errors.projectScale}</p>}
                  </div>
                  
                  <div>
                    <label htmlFor="timeline" className="block text-sm font-medium text-gray-300 mb-2">
                      Timeline
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-dark border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary"
                    >
                      <option value="">Select timeline</option>
                      <option value="urgent">Urgent (Within 1 month)</option>
                      <option value="short">Short term (1-3 months)</option>
                      <option value="medium">Medium term (3-6 months)</option>
                      <option value="long">Long term (6+ months)</option>
                      <option value="flexible">Flexible</option>
                    </select>
                    {errors.timeline && <p className="text-red-400 text-sm mt-1">{errors.timeline}</p>}
                  </div>
                </div>

                <div className="mb-6">
                  <label htmlFor="budget" className="block text-sm font-medium text-gray-300 mb-2">
                    Budget Range (Optional)
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-dark border border-gray-600 rounded-lg text-white focus:outline-none focus:border-primary"
                  >
                    <option value="">Select budget range</option>
                    <option value="under-10k">Under £10,000</option>
                    <option value="10k-25k">£10,000 - £25,000</option>
                    <option value="25k-50k">£25,000 - £50,000</option>
                    <option value="50k-100k">£50,000 - £100,000</option>
                    <option value="100k-plus">£100,000+</option>
                  </select>
                </div>

                <div className="mb-6">
                  <div id="turnstile-container"></div>
                  {errors.turnstile && <p className="text-red-400 text-sm mt-2">{errors.turnstile}</p>}
                </div>

                <div className="mb-6">
                  <label className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      name="privacy"
                      checked={formData.privacy}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                    <span className="text-sm text-gray-300">
                      I consent to nautdigital processing my data for the purpose of providing a technical assessment.
                      View our <a href="/privacy-policy" className="text-primary hover:underline">privacy policy</a>.
                    </span>
                  </label>
                  {errors.privacy && <p className="text-red-400 text-sm mt-1">{errors.privacy}</p>}
                </div>

                {errors.submit && (
                  <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-red-400 text-sm">{errors.submit}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn btn-primary text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Get My Free Technical Assessment'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default TechnicalAssessment;